/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  QuestionRiskOptions,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../../config";
import { debounce } from "lodash";
import MainBody from "../../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../../components/Pagination";
import sortCaret from "../../../../components/SortCaret";
import { ResponsiveDrawerForm } from "../../../../components/ResponsiveDrawerForm";
import { BadgeCheck, LucidePlusCircle } from "lucide-react";
import AddEditCondition from "./AddEditCondition";
import ConditionActionButton from "./ConditionActionButton";
import Confirmation from "../../../../components/Confirmation";
import ExpandConditions from "./ExpandConditions";
import { CaretDownIcon, CaretRightIcon } from "@radix-ui/react-icons";
import AdminFilters from "../../../../components/AdminFilters";

const Conditions = ({ expandList = false, conditionData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [conditionList, setConditionsList] = useState(
    expandList ? conditionData : []
  );

  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [conditionId, setConditionId] = useState(null);
  const [showAddEditCondition, setShowAddEditCondition] = useState(false);
  const [showConditionDelete, setShowConditionDelete] = useState(false);

  const [versionId, setVersionId] = useState();
  const [sectionType, setSectionType] = useState();
  const [organizationTypeId, setOrganizationTypeId] = useState();

  const toggleShowDelete = () => {
    if (showConditionDelete) {
      setConditionId(null);
    }
    setShowConditionDelete(!showConditionDelete);
  };

  const toggleShowAddEditCondition = () => {
    if (showAddEditCondition) {
      setEdit(false);
      setEditItem(null);
      setConditionId(null);
    }
    setShowAddEditCondition(!showAddEditCondition);
  };

  const fetchConditions = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_CONDITIONS}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }&organization_type_id=${
            organizationTypeId ? organizationTypeId : ""
          }&version_id=${versionId ? versionId : ""}&section_type=${
            sectionType ? sectionType : ""
          }`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const conditionList = data.data.data;
        setConditionsList(conditionList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setConditionsList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      searchParam,
      sortField,
      sortOrder,
      versionId,
      organizationTypeId,
      sectionType,
    ]
  );

  const conditionsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: conditionList[i]?.id,
        id: conditionList[i]?.id,
        organization_type: conditionList[i]?.organization_type?.name
          ? conditionList[i]?.organization_type?.name
          : "",
        version_number: conditionList[i]?.version?.version_number
          ? `${conditionList[i]?.version?.version_number} - ${conditionList[i]?.version?.name}`
          : "",
        order: conditionList[i]?.order ? conditionList[i]?.order : "",
        risk: (
          <div className="flex items-center w-full gap-2">
            <div
              style={{
                background: conditionList[i]?.color
                  ? conditionList[i]?.color
                  : "#ff0000",
              }}
              className={`h-6 w-6 rounded-md`}
            />
            <div>
              {conditionList[i]?.risk ? QuestionRiskOptions?.find((item) => item.value == conditionList[i]?.risk)?.label : ""}
            </div>
          </div>
        ),
        risk_action: conditionList[i]?.risk_action
          ? conditionList[i]?.risk_action
          : "",

        default: conditionList[i]?.default ? (
          <div className="flex items-center justify-center w-full">
            <BadgeCheck className="text-webPrimary" />
          </div>
        ) : (
          "-"
        ),

        section_type: conditionList[i]?.section_type
          ? conditionList[i]?.section_type
          : "",
        condition_details: conditionList[i]?.conditions
          ? conditionList[i]?.conditions
          : [],
        is_used: conditionList[i]?.is_used,
        action: (
          <>
            <ConditionActionButton
              id={conditionList[i]?.id}
              setEdit={setEdit}
              setConditionId={setConditionId}
              toggleShowAddEditCondition={toggleShowAddEditCondition}
              toggleShowDelete={toggleShowDelete}
              is_used={conditionList[i]?.is_used}
            />
          </>
        ),
      });
    }
    return items;
  };

  const conditions = conditionsGenerator(conditionList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "organization_type",
      text: "Industry Type",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "version_number",
      text: "Version Number",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "order",
      text: "Order",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "risk",
      text: "Risk Level",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "risk_action",
      text: "Risk Action",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "section_type",
      text: "Section Type",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "default",
      text: "Default",
      align: "center",
      headerAlign: "center",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    if (!expandList) {
      navigate(
        `?tab=risk-assessment&childTab=conditions&limit=${limit ? limit : defaultLimitPagination}&page=${
          page ? page : 1
        }&search=${searchParam ? searchParam : ""}&sort_column=${
          sortField ? sortField : ""
        }`,
        {
          replace: true,
        }
      );
    }
  }, [navigate, limit, searchParam, page, sortField, sortOrder, expandList]);

  useEffect(() => {
    if (!expandList) {
      fetchConditions();
    }
  }, [expandList, fetchConditions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchConditions(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchConditions(limit, page);
  };

  // Single Condition Delete
  const handleConditionDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.ADMIN_CONDITION_DELETE}/${conditionId}`);
      setLoading(false);
      fetchConditions();
    } catch (error) {
      setLoading(false);
    }
  };

  const expandRow = {
    renderer: (row) => (
      <>
        <ExpandConditions
          expandList={true}
          expandData={row?.condition_details}
        />
      </>
    ),
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandByColumnOnly: true,
    headerClasses: THClasses,
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <CaretDownIcon />;
      }
      return <CaretRightIcon />;
    },
    className: TDClasses,
    expandHeaderColumnRenderer: () => {
      return <></>;
    },
  };

  return (
    <>
      <MainBody
        search={!expandList}
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          !expandList && (
            <div className="flex items-center gap-3 flex-wrap">
              <AdminFilters
                organizationType={true}
                setOrganizationTypeId={setOrganizationTypeId}
                organizationTypeId={organizationTypeId}
                version={true}
                setVersionId={setVersionId}
                versionId={versionId}
                sectionTypeSelect={true}
                setSectionType={setSectionType}
                sectionType={sectionType}
              />
              {/* Add Condition */}
              <ResponsiveDrawerForm
                description={""}
                title={edit ? "Edit Condition(s)" : "Add Condition(s)"}
                handleOpen={() => {}}
                handleClose={() => {
                  setEdit(false);
                  setConditionId();
                  setEditItem(null);
                }}
                buttonText={
                  <div className="flex items-center gap-1">
                    <LucidePlusCircle size={18} />
                    <span>Add Condition</span>
                  </div>
                }
                open={showAddEditCondition}
                buttonClassName={""}
                setOpen={setShowAddEditCondition}
                mobileCancelHidden={true}
              >
                <AddEditCondition
                  edit={edit}
                  editItem={editItem}
                  toggleShowAddEditCondition={toggleShowAddEditCondition}
                  id={conditionId}
                  fetchConditionData={() => fetchConditions(limit, page)}
                />
              </ResponsiveDrawerForm>
            </div>
          )
        }
        loading={loading}
        innerBody={true}
        expandList={true}
        className="p-0 pt-1"
        mainClassName="w-[calc(100%-3rem)]"
      >
        <div className="custom-table">
          <div className="table-body-class max-h-[550px]">
            <BootstrapTable
              keyField="key"
              remote
              data={conditions}
              columns={columns}
              expandRow={expandRow}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
          <Confirmation
            title={"Delete Condition"}
            open={showConditionDelete}
            setOpen={setShowConditionDelete}
            toggleShow={() => {
              toggleShowDelete();
            }}
            className="sm:max-w-1/2"
            message={"Are you sure you want to delete condition?"}
            okAction={async () => {
              handleConditionDelete();
              toggleShowDelete();
              fetchConditions();
            }}
            cancelAction={() => {
              setConditionId(null);
              setShowConditionDelete(!showConditionDelete);
            }}
          />
        </div>
      </MainBody>
    </>
  );
};

export default Conditions;
